import { prefersReducedMotion } from '@theme/utilities';

/**
 * <mqc-hero-render> — "Si es ___, es Tree Shop" 3D phone hero.
 *
 * Performance architecture (analogous to React.memo):
 *
 *  1. Memoized style writes — style.transform is only committed to the DOM
 *     when the rounded value actually changes. Avoids layout work on frames
 *     where the phone hasn't moved perceptibly (idle spin convergence, etc.).
 *
 *  2. Pausable rAF loop — the tick loop stops itself when the hero leaves the
 *     viewport and restarts via IntersectionObserver or user interaction.
 *     Saves CPU/battery during scroll-past.
 *
 *  3. Time-delta lerp — lerp factor is normalised to 1/60 s so it feels
 *     identical at 60 Hz, 90 Hz, and 120 Hz (ProMotion) displays.
 *
 *  4. Mobile intro skip — on touch/narrow screens the 8-second cinematic
 *     intro is bypassed; the phone renders in its resting state immediately.
 *
 *  5. Stage-scoped pointer capture — pointermove / pointerup listen on the
 *     stage element (not window) after setPointerCapture, so the global event
 *     bus is not polluted with per-frame pointer events during drag.
 */
class MqcHeroRender extends HTMLElement {
  connectedCallback() {
    this.reduced = prefersReducedMotion();

    this.rig            = this.querySelector('.mqc-hero-render__rig');
    this.rigWrap        = this.querySelector('.mqc-hero-render__rig-wrap');
    this.sheen          = this.querySelector('.mqc-hero-render__sheen');
    this.stage          = this.querySelector('.mqc-hero-render__stage');
    this.veil           = this.querySelector('.mqc-hero-render__veil');
    this.orbitRing      = this.querySelector('.mqc-hero-render__orbit');
    this.pedestal       = this.querySelector('.mqc-hero-render__pedestal');
    this.pedestalShadow = this.querySelector('.mqc-hero-render__pedestal-shadow');
    this.copy           = this.querySelector('.mqc-hero-render__copy');
    this.dragHint       = this.querySelector('.mqc-hero-render__draghint');
    this.scrollCue      = this.querySelector('.mqc-hero-render__scrollcue');
    this.cycleWord      = this.querySelector('[data-hero-cycle-word]');
    this.ctaPrimary     = this.querySelector('[data-hero-magnetic]');
    this.model          = this.querySelector('.mqc-hero-render__model'); // cached ref
    this.scrollSection  = this.closest('.mqc-hero-render-scroll') || this;

    if (!this.rig || !this.rigWrap) return;

    this.words = (this.cycleWord?.getAttribute('data-words') || '')
      .split('|').map((w) => w.trim()).filter(Boolean);

    // Physics state
    this.idleY       = 0;
    this.hoverX      = 0;
    this.hoverY      = 0;
    this.dragBaseY   = 0;
    this.dragOffsetX = 0;
    this.dragOffsetY = 0;
    this.scrollY     = 0;
    this.scrollX     = 0;
    this.scrollScale = 1;
    this.curX        = -8;
    this.curY        = 18;
    this.isDragging  = false;
    this.isInView    = true;

    // ── Memoization: last committed DOM values ──────────────────────────────
    // Only write style when rounded output differs from what was last written.
    this._memo = { rigX: null, rigY: null, scale: null, sheenX: null };
    this._rafRunning = false;
    this._lastTick   = 0;

    // Device detection
    this.isTouch   = 'ontouchstart' in window && navigator.maxTouchPoints > 0;
    // Skip 8-second cinematic intro on touch/mobile — phone appears instantly
    this.skipIntro = this.isTouch || window.innerWidth < 768;

    this.introActive      = !this.reduced && !this.skipIntro;
    this.introStart       = null;
    this.pedestalRevealed = false;
    this.textRevealed     = false;
    this.lastIntroRotX    = -8;
    this.lastIntroRotY    = 18;
    this.brandIdx         = 0;

    this.#bind();
    this.#startTrustCycle();

    this.io = new IntersectionObserver((entries) => {
      this.isInView = entries[0].isIntersecting;
      if (this.isInView) this.#startRaf(); // resume if loop was paused
    }, { threshold: 0 });
    this.io.observe(this.scrollSection);

    if (this.reduced || this.skipIntro) {
      this.#landOnRestingState();
      if (!this.reduced) this.#startRaf();
      return;
    }

    this.#startRaf();
  }

  // Idempotent rAF starter — never double-fires the loop
  #startRaf() {
    if (this._rafRunning) return;
    this._rafRunning = true;
    requestAnimationFrame(this.tick);
  }

  #startTrustCycle() {
    const cards = this.querySelectorAll('.mqc-hero-render__trust-card');
    const dots  = this.querySelectorAll('.mqc-hero-render__dot');
    if (!cards.length) return;

    cards[0].classList.add('is-active');
    dots[0]?.classList.add('is-active');
    if (this.reduced) return;

    let i = 0;
    this.trustInterval = setInterval(() => {
      cards.forEach((c) => c.classList.remove('is-active'));
      dots.forEach((d) => d.classList.remove('is-active'));
      i = (i + 1) % cards.length;
      cards[i].classList.add('is-active');
      dots[i]?.classList.add('is-active');
    }, 2400);
  }

  disconnectedCallback() {
    this.io?.disconnect();
    if (this.trustInterval) clearInterval(this.trustInterval);
    if (this.inertiaRaf)    cancelAnimationFrame(this.inertiaRaf);
    window.removeEventListener('scroll', this.onScroll);
  }

  #bind() {
    this.tick        = this.tick.bind(this);
    this.onScroll    = this.onScroll.bind(this);
    this.pointerDown = this.pointerDown.bind(this);
    this.pointerMove = this.pointerMove.bind(this);
    this.pointerUp   = this.pointerUp.bind(this);

    // Hover tilt + magnetic CTA — desktop (fine pointer) only
    if (!this.reduced && this.stage && window.matchMedia('(pointer: fine)').matches) {
      this.stage.addEventListener('mousemove', (e) => {
        if (this.isDragging) return;
        const rect  = this.stage.getBoundingClientRect();
        this.hoverX = -(((e.clientY - rect.top)  / rect.height) - 0.5) * 16;
        this.hoverY =  (((e.clientX - rect.left) / rect.width)  - 0.5) * 16;
      });
      this.stage.addEventListener('mouseleave', () => { this.hoverX = 0; this.hoverY = 0; });

      this.ctaPrimary?.addEventListener('mousemove', (e) => {
        const rect = this.ctaPrimary.getBoundingClientRect();
        this.ctaPrimary.style.setProperty('--mx', `${((e.clientX - rect.left - rect.width  / 2) * 0.18).toFixed(1)}px`);
        this.ctaPrimary.style.setProperty('--my', `${((e.clientY - rect.top  - rect.height / 2) * 0.28).toFixed(1)}px`);
      });
      this.ctaPrimary?.addEventListener('mouseleave', () => {
        this.ctaPrimary.style.setProperty('--mx', '0px');
        this.ctaPrimary.style.setProperty('--my', '0px');
      });
    }

    if (!this.reduced && this.stage) {
      // Drag listeners on the stage itself.
      // After setPointerCapture in pointerDown, the browser redirects all
      // subsequent pointer events to this element even when the pointer
      // moves outside it — so we never need to listen on window.
      this.stage.addEventListener('pointerdown',   this.pointerDown);
      this.stage.addEventListener('pointermove',   this.pointerMove);
      this.stage.addEventListener('pointerup',     this.pointerUp);
      this.stage.addEventListener('pointercancel', this.pointerUp); // interrupted by system (calls, etc.)
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }

  #landOnRestingState() {
    this.rig.style.transform = 'rotateX(-8deg) rotateY(18deg)';
    // Seed the memo cache so the first interactive tick never re-commits the same value
    this._memo.rigX = -8; this._memo.rigY = 18;
    if (this.veil) { this.veil.style.opacity = '0'; this.veil.style.pointerEvents = 'none'; }
    this.orbitRing?.classList.add('is-visible');
    this.pedestal?.classList.add('is-visible');
    this.pedestalShadow?.classList.add('is-visible');
    this.copy?.classList.add('is-visible');
    if (this.dragHint) this.dragHint.style.display = 'none';
  }

  static easeInOutCubic(t) { return t < 0.5 ? 4*t*t*t : 1-((-2*t+2)**3)/2; }

  #revealPedestal() {
    if (this.pedestalRevealed) return;
    this.pedestalRevealed = true;
    this.orbitRing?.classList.add('is-visible');
    this.pedestal?.classList.add('is-visible');
    this.pedestalShadow?.classList.add('is-visible');
  }

  #revealText() {
    if (this.textRevealed) return;
    this.textRevealed = true;
    this.copy?.classList.add('is-visible');
  }

  #endIntro() {
    if (!this.introActive) return;
    this.introActive = false;
    if (this.veil) { this.veil.style.opacity = '0'; this.veil.style.pointerEvents = 'none'; }
    this.#revealPedestal();
    this.#revealText();
    this.dragHint?.classList.add('is-visible');
    this.idleY = this.lastIntroRotY;
    this.curY  = this.lastIntroRotY;
    this.curX  = this.lastIntroRotX;
  }

  #runIntroFrame(elapsed) {
    const ease = MqcHeroRender.easeInOutCubic;
    let t, rotX, rotY, scale;

    if (elapsed < 1000) {
      t = ease(elapsed / 1000);
      if (this.veil) {
        this.veil.style.opacity   = String(1 - t);
        this.veil.style.transform = `translate(-50%,-50%) scale(${(1 + t * 0.45).toFixed(3)})`;
      }
      this.rigWrap.style.transform = 'translateY(24px) scale(.92)';
      rotX = -8; rotY = 0;
    } else if (elapsed < 2000) {
      t = ease((elapsed - 1000) / 1000);
      this.#revealPedestal();
      this.rigWrap.style.transform = `translateY(${(24*(1-t)).toFixed(1)}px) scale(${(0.92+0.08*t).toFixed(3)})`;
      rotX = -8; rotY = t * 20;
    } else if (elapsed < 4000) {
      t = ease((elapsed - 2000) / 2000);
      this.rigWrap.style.transform = 'translateY(0px) scale(1)';
      rotX = -8; rotY = 20 + t * 140;
    } else if (elapsed < 5000) {
      t = ease((elapsed - 4000) / 1000);
      scale = 1 + t * 0.24;
      this.rigWrap.style.transform = `translateY(0px) scale(${scale.toFixed(3)})`;
      rotX = -8 - t * 3; rotY = 160 + t * 15;
    } else if (elapsed < 6000) {
      t = ease((elapsed - 5000) / 1000);
      scale = 1.24 - t * 0.24;
      this.rigWrap.style.transform = `translateY(0px) scale(${scale.toFixed(3)})`;
      rotX = -11 + t * 3; rotY = 175 + t * 30;
    } else {
      t = ease((elapsed - 6000) / 2000);
      this.rigWrap.style.transform = 'translateY(0px) scale(1)';
      rotX = -8; rotY = 205 + (18 - 205) * t;
      if (elapsed - 6000 > 250) this.#revealText();
    }

    this.rig.style.transform = `rotateX(${rotX.toFixed(2)}deg) rotateY(${rotY.toFixed(2)}deg)`;
    if (this.sheen) this.sheen.style.transform = `translateX(${(rotY * 2.1).toFixed(1)}px)`;
    this.lastIntroRotX = rotX;
    this.lastIntroRotY = rotY;
  }

  tick(now) {
    if (this.introStart === null) this.introStart = now;

    if (this.introActive) {
      const elapsed = now - this.introStart;
      if (elapsed >= 8000) {
        this.#endIntro();
      } else {
        this.#runIntroFrame(elapsed);
        requestAnimationFrame(this.tick);
        return;
      }
    }

    // ── Pause when off-screen: stop the loop, IntersectionObserver restarts it ──
    if (!this.isInView && !this.isDragging) {
      this._rafRunning = false;
      return;
    }

    // ── Time-delta lerp: identical feel at 60 / 90 / 120 Hz ──────────────────
    // Without dt normalisation a 120Hz ProMotion display applies the lerp
    // factor twice per 60Hz frame, making interactions feel twice as sluggish.
    const dt        = Math.min(now - (this._lastTick || now), 50); // cap spikes (tab switch, etc.)
    this._lastTick  = now;
    const BASE_LERP = this.isDragging && this.isTouch ? 0.22 : 0.08;
    const lerp      = 1 - Math.pow(1 - BASE_LERP, dt / (1000 / 60));

    if (this.isInView && !this.isDragging) this.idleY += 0.035;

    const targetY = this.idleY + this.dragBaseY + this.dragOffsetY + this.hoverY * 0.5 + this.scrollY;
    const targetX = -8 + this.hoverX * 0.6 + this.dragOffsetX * 0.6 + this.scrollX;

    this.curX += (targetX - this.curX) * lerp;
    this.curY += (targetY - this.curY) * lerp;

    // ── Memoized style writes ─────────────────────────────────────────────────
    // Round first, then compare. Only call style.transform when the committed
    // value actually changes — equivalent to React.memo's shallow prop check.
    const rx = +this.curX.toFixed(2);
    const ry = +this.curY.toFixed(2);
    const rs = +this.scrollScale.toFixed(3);
    const sh = +(this.curY * 2.1).toFixed(1);

    if (rx !== this._memo.rigX || ry !== this._memo.rigY) {
      this.rig.style.transform = `rotateX(${rx}deg) rotateY(${ry}deg)`;
      this._memo.rigX = rx;
      this._memo.rigY = ry;
    }
    if (rs !== this._memo.scale) {
      this.rigWrap.style.transform = `scale(${rs})`;
      this._memo.scale = rs;
    }
    if (this.sheen && sh !== this._memo.sheenX) {
      this.sheen.style.transform = `translateX(${sh}px)`;
      this._memo.sheenX = sh;
    }

    requestAnimationFrame(this.tick);
  }

  pointerDown(e) {
    // When the real GLTF model is loaded it owns drag — don't also capture here
    if (this.model?.classList.contains('is-loaded')) return;
    if (this.introActive) this.#endIntro();
    this.isDragging = true;
    this.stage.classList.add('is-dragging');
    if (this.inertiaRaf) { cancelAnimationFrame(this.inertiaRaf); this.inertiaRaf = null; }
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;
    this.velocity   = 0;
    this.lastDx     = 0;
    if (this.dragHint) this.dragHint.style.opacity = '0';
    // Capture redirects subsequent pointermove/up to this.stage (see #bind)
    if (this.stage.setPointerCapture && e.pointerId != null) this.stage.setPointerCapture(e.pointerId);
    this.#startRaf(); // resume loop if it was paused by off-screen exit
  }

  pointerMove(e) {
    if (!this.isDragging) return;
    const dx         = e.clientX - this.dragStartX;
    const dy         = e.clientY - this.dragStartY;
    this.dragOffsetY = dx * 0.35;
    this.dragOffsetX = Math.max(-20, Math.min(20, -dy * 0.25));
    this.velocity    = dx - this.lastDx;
    this.lastDx      = dx;
  }

  pointerUp() {
    if (!this.isDragging) return;
    this.isDragging  = false;
    this.stage.classList.remove('is-dragging');
    this.dragBaseY  += this.dragOffsetY;
    this.dragOffsetY = 0;

    let v = (this.velocity || 0) * 0.6;
    const inertiaStep = () => {
      v *= 0.94;
      this.dragBaseY += v * 0.35;
      if (Math.abs(v) > 0.05) {
        this.inertiaRaf = requestAnimationFrame(inertiaStep);
      } else {
        this.inertiaRaf  = null;
        this.dragOffsetX *= 0.8;
      }
    };
    inertiaStep();
  }

  onScroll() {
    if (this.scrollTicking || !this.isInView) return;
    this.scrollTicking = true;
    requestAnimationFrame(() => {
      const rect     = this.scrollSection.getBoundingClientRect();
      const total    = rect.height - window.innerHeight;
      const progress = total > 0 ? Math.min(Math.max(-rect.top / total, 0), 1) : 0;

      this.scrollY     = progress * 70;
      this.scrollX     = progress * -6;
      this.scrollScale = 1 + progress * 0.1;
      if (this.scrollCue) this.scrollCue.style.opacity = progress > 0.05 ? '0' : '1';

      if (this.words.length) {
        const idx = Math.min(this.words.length - 1, Math.floor(progress * this.words.length));
        if (idx !== this.brandIdx) {
          this.brandIdx = idx;
          this.#swapBrand(this.words[idx]);
        }
      }
      this.scrollTicking = false;
    });
  }

  #swapBrand(name) {
    if (!this.cycleWord) return;
    this.cycleWord.style.transition  = 'opacity .25s ease, transform .25s ease, filter .25s ease';
    this.cycleWord.style.opacity     = '0';
    this.cycleWord.style.transform   = 'translateY(6px)';
    this.cycleWord.style.filter      = 'blur(6px)';
    setTimeout(() => {
      this.cycleWord.textContent     = name.toUpperCase();
      this.cycleWord.style.opacity   = '1';
      this.cycleWord.style.transform = 'translateY(0)';
      this.cycleWord.style.filter    = 'blur(0)';
    }, 220);

    this.querySelectorAll('[data-brand-pill]').forEach((pill) => {
      pill.classList.toggle('is-active', pill.getAttribute('data-brand-pill').toLowerCase() === name.toLowerCase());
    });
  }
}

if (!customElements.get('mqc-hero-render')) {
  customElements.define('mqc-hero-render', MqcHeroRender);
}
